import type { ContactErrors, ContactField, ContactValues } from '@/types'

import { COMPANY_SIZES, CONTACT_LIMITS, CONTACT_TOPICS } from '@/constants/contact'

export const CONTACT_FIELDS: Array<ContactField> = ['email', 'company', 'firstName', 'lastName', 'companySize', 'topic', 'message']

export const EMPTY_CONTACT: ContactValues = {
    email: '',
    company: '',
    firstName: '',
    lastName: '',
    companySize: '',
    topic: '',
    message: ''
}

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/
const LINE_CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/g
const TEXT_CONTROL_CHARACTERS = /[\u0000-\u0008\u000B-\u001F\u007F]/g

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

// Single-line values: collapse every run of whitespace, drop control characters.
const cleanLine = (value: unknown): string =>
    typeof value === 'string' ? value.replace(/\s+/g, ' ').replace(LINE_CONTROL_CHARACTERS, '').trim() : ''

// Multi-line values: keep line breaks and tabs, normalize Windows line endings.
const cleanText = (value: unknown): string =>
    typeof value === 'string' ? value.replace(/\r\n?/g, '\n').replace(TEXT_CONTROL_CHARACTERS, '').trim() : ''

export const normalizeContact = (input: unknown): ContactValues => {
    const source = isRecord(input) ? input : {}

    return {
        email: cleanLine(source.email),
        company: cleanLine(source.company),
        firstName: cleanLine(source.firstName),
        lastName: cleanLine(source.lastName),
        companySize: cleanLine(source.companySize),
        topic: cleanLine(source.topic),
        message: cleanText(source.message)
    }
}

const validateField = (field: ContactField, value: string): string | undefined => {
    switch (field) {
        case 'email':
            if (!value) return 'Enter your work email.'
            if (value.length > CONTACT_LIMITS.email || !EMAIL_PATTERN.test(value)) return 'Enter a valid email, like jane@company.com.'
            return undefined

        case 'company':
            if (value.length > CONTACT_LIMITS.company) return `Keep the company name under ${CONTACT_LIMITS.company} characters.`
            return undefined

        case 'firstName':
            if (!value) return 'Enter your first name.'
            if (value.length > CONTACT_LIMITS.name) return `Keep your first name under ${CONTACT_LIMITS.name} characters.`
            return undefined

        case 'lastName':
            if (!value) return 'Enter your last name.'
            if (value.length > CONTACT_LIMITS.name) return `Keep your last name under ${CONTACT_LIMITS.name} characters.`
            return undefined

        case 'companySize':
            if (value && !COMPANY_SIZES.includes(value)) return 'Choose a company size from the list.'
            return undefined

        case 'topic':
            if (!CONTACT_TOPICS.includes(value)) return 'Choose a topic.'
            return undefined

        case 'message':
            if (!value) return 'Tell us a little about what you need.'
            if (value.length < CONTACT_LIMITS.messageMin) return `Add a few more details, at least ${CONTACT_LIMITS.messageMin} characters.`
            if (value.length > CONTACT_LIMITS.messageMax) return 'Keep your message under 3,000 characters.'
            return undefined
    }
}

export const validateContact = (values: ContactValues, fields: Array<ContactField> = CONTACT_FIELDS): ContactErrors => {
    const errors: ContactErrors = {}

    for (const field of fields) {
        const error = validateField(field, values[field])

        if (error) errors[field] = error
    }

    return errors
}

export const hasErrors = (errors: ContactErrors): boolean => Object.keys(errors).length > 0
